import { useMemo, useState } from 'react';
import { Key, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@cimpress-ui/react';
import { DecisionViewerProps } from './DecisionViewer';
import { SpinnerWrapper } from './SpinnerWrapper';
import { DecisionTab } from './DecisionTab';
import { useOrchestrateRoutingItem } from '../hooks/useOrchestrateRoutingItem';
import { useAsyncError } from '../hooks/useAsyncError';

export const DecisionViewerBody = ({
  decisionLink,
  showDecisionLinks,
  item,
  decisionJson,
  halfWeight,
  routingReceipts,
  idpResults,
}: DecisionViewerProps) => {
  // Default to selecting the current decision tab
  const [selectedIndex, setSelectedIndex] = useState<Key>(1);

  const throwAsyncError = useAsyncError();

  const { data, isLoading, error } = useOrchestrateRoutingItem({
    item,
    currentRoutingDecisionUrl: decisionLink,
    currentRoutingDecision: decisionJson,
    showDecisionLinks,
    currentRoutingReceipts: routingReceipts,
    currentIDPResults: idpResults,
  });

  const {
    currentItem,
    initialItem,
    initialItemRoutingRequestUrl,
    initialRoutingDecisionUrl,
    initialRoutingDecision,
    initialRoutingReceipts,
    currentItemRoutingRequestUrl,
    currentRoutingDecision,
    currentRoutingDecisionUrl,
    currentRoutingReceipts,
    currentIDPResults,
    diff,
  } = data || {};

  const tabs = useMemo(
    () => [
      {
        name: 'Initial Decision',
        block: (
          <div>
            <Text as="p" variant="medium" marginLeft={12}>
              Represents the decision between the buyer and seller.
            </Text>
            <DecisionTab
              item={initialItem}
              decisionLink={initialRoutingDecisionUrl}
              routingDecision={initialRoutingDecision}
              routingRequestUrl={initialItemRoutingRequestUrl}
              showDecisionLinks={showDecisionLinks}
              routingReceipts={initialRoutingReceipts}
              halfWeight={halfWeight}
              diff={diff}
            />
          </div>
        ),
        href: '#',
      },
      {
        name: 'Current Decision',
        block: (
          <div>
            <Text as="p" variant="medium" marginLeft={12}>
              Represents the decision between the seller and their third-party fulfillers (3PF). The seller can choose
              themself or a 3PF.
            </Text>
            <DecisionTab
              item={currentItem}
              routingDecision={currentRoutingDecision}
              decisionLink={currentRoutingDecisionUrl || currentRoutingDecision?._links?.self?.href}
              routingRequestUrl={currentItemRoutingRequestUrl}
              showDecisionLinks={showDecisionLinks}
              halfWeight={halfWeight}
              routingReceipts={currentRoutingReceipts}
              idpResults={currentIDPResults}
              diff={diff}
            />
          </div>
        ),
        href: '#',
      },
    ],
    [
      initialItem,
      initialItemRoutingRequestUrl,
      initialRoutingDecision,
      initialRoutingDecisionUrl,
      initialRoutingReceipts,
      currentItem,
      currentRoutingDecision,
      currentRoutingDecisionUrl,
      currentItemRoutingRequestUrl,
      currentRoutingReceipts,
      currentIDPResults,
      showDecisionLinks,
      diff,
      halfWeight,
    ],
  );

  if (isLoading) {
    return <SpinnerWrapper />;
  }

  if (error) {
    throwAsyncError(error.error, error.context);
  }

  return (
    <div data-cim-style-root>
      {initialRoutingDecisionUrl && (
        <div>
          <Tabs aria-label="Decision tabs" selectedKey={selectedIndex} onSelectionChange={setSelectedIndex}>
            <TabList>
              {tabs.map((tab, index) => (
                <Tab key={`${index}-tab`} id={index}>
                  {tab.name}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              {tabs.map((tab, index) => (
                <TabPanel key={`${index}-panel`} id={index}>
                  {tab.block}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </div>
      )}
      {!initialRoutingDecisionUrl && (
        <DecisionTab
          item={currentItem}
          routingDecision={currentRoutingDecision}
          decisionLink={currentRoutingDecisionUrl || currentRoutingDecision?._links?.self?.href}
          routingRequestUrl={currentItemRoutingRequestUrl}
          showDecisionLinks={showDecisionLinks}
          routingReceipts={currentRoutingReceipts}
          idpResults={currentIDPResults}
          halfWeight={halfWeight}
          diff={diff}
        />
      )}
    </div>
  );
};
